// src/components/Packaging/PackagingRuleModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { usePackaging } from "../../contexts/PackagingContext";

function num(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}
function perUnitBase(item) {
  const mode = item?.pricingMode || "unit";
  if (mode === "unit") return num(item?.unitPrice, 0);
  const packPrice = num(item?.packPrice, 0);
  const unitsPerPack = Math.max(1, num(item?.unitsPerPack, 1));
  return packPrice / unitsPerPack;
}

export default function PackagingRuleModal({
  initialRule,
  onSave,
  onClose,
  /** Optional: pass items in yourself; if omitted, we’ll read from context */
  items: itemsProp,
}) {
  const { items: itemsFromCtx = [] } = usePackaging() || {};
  const items = useMemo(
    () => (Array.isArray(itemsProp) ? itemsProp : itemsFromCtx) || [],
    [itemsProp, itemsFromCtx]
  );

  // ----- Tax display config from CustomizeModal -----
  const [taxName, setTaxName] = useState("GST");
  const [taxRatePct, setTaxRatePct] = useState(10);
  const [taxModeDefault, setTaxModeDefault] = useState("inclusive");
  const [displayTaxMode, setDisplayTaxMode] = useState("inclusive");

  useEffect(() => {
    const onTax = (e) => {
      const { taxName, taxRatePct, taxModeDefault } = e.detail || {};
      if (taxName) setTaxName(taxName);
      if (typeof taxRatePct === "number") setTaxRatePct(taxRatePct);
      if (taxModeDefault) {
        setTaxModeDefault(taxModeDefault);
        setDisplayTaxMode((prev) =>
          prev === "inclusive" || prev === "exclusive" || prev === "none"
            ? prev
            : taxModeDefault
        );
      }
    };
    document.addEventListener("mudo:taxSettingsChanged", onTax);
    return () => document.removeEventListener("mudo:taxSettingsChanged", onTax);
  }, []);

  const [form, setForm] = useState(() => ({
    id: initialRule?.id ?? undefined,
    scope: initialRule?.scope ?? "global", // global | product | batch
    productId: initialRule?.productId ?? "",
    batchId: initialRule?.batchId ?? "",
    itemId: initialRule?.itemId ?? (items[0]?.id ?? ""),
    appliesAs: initialRule?.appliesAs ?? "perUnit", // perUnit | perBatch | threshold
    qtyPerUnit: num(initialRule?.qtyPerUnit, 1),
    qtyPerBatch: num(initialRule?.qtyPerBatch, 1),
    threshold: initialRule?.threshold ?? { minQty: 1, step: 1, perStepQty: 1 },
    rounding: initialRule?.rounding ?? "none", // none | up | down
    active: initialRule?.active ?? true,
    notes: initialRule?.notes ?? "",
  }));

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const updateThreshold = (patch) =>
    setForm((prev) => ({ ...prev, threshold: { ...prev.threshold, ...patch } }));

  // Derived flags
  const isPerUnit = form.appliesAs === "perUnit";
  const isPerBatch = form.appliesAs === "perBatch";
  const isThreshold = form.appliesAs === "threshold";

  // Validation
  const problems = [];
  if (!form.itemId) problems.push("Item is required.");
  if (form.scope === "product" && !String(form.productId).trim())
    problems.push("Product ID is required for product-scoped rules.");
  if (form.scope === "batch" && !String(form.batchId).trim())
    problems.push("Batch ID is required for batch-scoped rules.");
  if (isPerUnit && num(form.qtyPerUnit) <= 0) problems.push("Quantity per unit must be greater than 0.");
  if (isPerBatch && num(form.qtyPerBatch) < 0) problems.push("Quantity per batch cannot be negative.");
  if (isThreshold) {
    const { minQty, step, perStepQty } = form.threshold || {};
    if (num(minQty) < 0) problems.push("Threshold: min quantity cannot be negative.");
    if (num(step) <= 0) problems.push("Threshold: step must be greater than 0.");
    if (num(perStepQty) <= 0) problems.push("Threshold: qty per step must be greater than 0.");
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (problems.length) return;
    const normalized = {
      ...form,
      qtyPerUnit: num(form.qtyPerUnit, 0),
      qtyPerBatch: num(form.qtyPerBatch, 0),
      threshold: isThreshold
        ? {
            minQty: num(form.threshold?.minQty, 0),
            step: num(form.threshold?.step, 1),
            perStepQty: num(form.threshold?.perStepQty, 1),
          }
        : null,
    };
    onSave?.(normalized);
  };

  // ----- Cost hint (based on selected item) -----
  const item = useMemo(() => items.find((i) => i.id === form.itemId) || null, [items, form.itemId]);
  const unitBase = useMemo(() => perUnitBase(item), [item]);

  const effectiveTaxFlag = (item?.taxFlag || "").toLowerCase() || taxModeDefault || "inclusive";
  const rate = Math.max(0, Number(taxRatePct) || 0) / 100;

  function priceView(base, viewMode) {
    if (viewMode === "none" || effectiveTaxFlag === "none") return base;
    if (effectiveTaxFlag === "inclusive" && viewMode === "inclusive") return base;
    if (effectiveTaxFlag === "exclusive" && viewMode === "exclusive") return base;
    if (effectiveTaxFlag === "inclusive" && viewMode === "exclusive") return (1 + rate) > 0 ? base / (1 + rate) : base;
    if (effectiveTaxFlag === "exclusive" && viewMode === "inclusive") return base * (1 + rate);
    return base;
  }

  // naive illustrative costs (for UX feedback)
  const sampleUnits = 10; // example “units produced” to show threshold math contextually
  const qtyUsed = useMemo(() => {
    if (!item) return 0;
    if (isPerUnit) return num(form.qtyPerUnit, 0) * sampleUnits;
    if (isPerBatch) return num(form.qtyPerBatch, 0);
    if (isThreshold) {
      const t = form.threshold || {};
      const minQ = num(t.minQty, 0);
      const step = Math.max(1, num(t.step, 1));
      const perStep = num(t.perStepQty, 1);
      if (sampleUnits < minQ) return 0;
      const steps = Math.floor((sampleUnits - minQ) / step) + 1;
      return steps * perStep;
    }
    return 0;
  }, [item, isPerUnit, isPerBatch, isThreshold, form]);

  const costEx = priceView(unitBase, "exclusive") * qtyUsed;
  const costIn = priceView(unitBase, "inclusive") * qtyUsed;

  // Shared input styles (force contrast for both themes)
  const inputCls =
    "w-full rounded border px-3 py-2 " +
    "bg-white text-neutral-900 placeholder:text-neutral-500 border-neutral-300 " +
    "dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-400 dark:border-neutral-700 " +
    "focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent";
  const labelCls = "block text-sm font-medium mb-1 text-neutral-700 dark:text-neutral-300";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      {/* Force readable text colors on the panel */}
      <div className="w-[560px] max-w-full rounded-xl border shadow-lg
                      bg-white text-neutral-900 border-neutral-200
                      dark:bg-neutral-900 dark:text-white dark:border-white/10 p-6">
        <h2 className="text-lg font-bold mb-4">
          {initialRule?.id ? "Edit Packaging Rule" : "New Packaging Rule"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Scope */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3 sm:col-span-1">
              <label className={labelCls}>Scope</label>
              <select
                value={form.scope}
                onChange={(e) => update({ scope: e.target.value })}
                className={inputCls}
              >
                <option value="global">Global</option>
                <option value="product">Product</option>
                <option value="batch">Batch</option>
              </select>
            </div>

            {form.scope === "product" && (
              <div className="col-span-3 sm:col-span-2">
                <label className={labelCls}>Product ID</label>
                <input
                  type="text"
                  value={form.productId}
                  onChange={(e) => update({ productId: e.target.value })}
                  className={inputCls}
                  placeholder="e.g. PROD-123"
                />
              </div>
            )}

            {form.scope === "batch" && (
              <div className="col-span-3 sm:col-span-2">
                <label className={labelCls}>Batch ID</label>
                <input
                  type="text"
                  value={form.batchId}
                  onChange={(e) => update({ batchId: e.target.value })}
                  className={inputCls}
                  placeholder="e.g. BATCH-2025-01"
                />
              </div>
            )}
          </div>

          {/* Item */}
          <div>
            <label className={labelCls}>Item</label>
            {items.length ? (
              <select
                value={form.itemId}
                onChange={(e) => update({ itemId: e.target.value })}
                className={inputCls}
              >
                {items.map((it) => (
                  <option
                    key={it.id}
                    value={it.id}
                    className="text-neutral-900 dark:text-white"
                  >
                    {it.name || "Untitled"} {it.category ? `• ${it.category}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={form.itemId}
                onChange={(e) => update({ itemId: e.target.value })}
                className={inputCls}
                placeholder="Item ID"
              />
            )}
          </div>

          {/* Applies as */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3 sm:col-span-1">
              <label className={labelCls}>Applies as</label>
              <select
                value={form.appliesAs}
                onChange={(e) => update({ appliesAs: e.target.value })}
                className={inputCls}
              >
                <option value="perUnit">Per Unit</option>
                <option value="perBatch">Per Batch</option>
                <option value="threshold">Threshold</option>
              </select>
            </div>

            {form.appliesAs === "perUnit" && (
              <div className="col-span-3 sm:col-span-2">
                <label className={labelCls}>Qty per unit</label>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={form.qtyPerUnit}
                  onChange={(e) => update({ qtyPerUnit: e.target.value })}
                  className={inputCls}
                />
              </div>
            )}

            {form.appliesAs === "perBatch" && (
              <div className="col-span-3 sm:col-span-2">
                <label className={labelCls}>Qty per batch</label>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={form.qtyPerBatch}
                  onChange={(e) => update({ qtyPerBatch: e.target.value })}
                  className={inputCls}
                />
              </div>
            )}
          </div>

          {form.appliesAs === "threshold" && (
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-3 sm:col-span-1">
                <label className={labelCls}>Min qty</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.threshold?.minQty ?? 0}
                  onChange={(e) => updateThreshold({ minQty: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div className="col-span-3 sm:col-span-1">
                <label className={labelCls}>Step</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.threshold?.step ?? 1}
                  onChange={(e) => updateThreshold({ step: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div className="col-span-3 sm:col-span-1">
                <label className={labelCls}>Qty per step</label>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={form.threshold?.perStepQty ?? 1}
                  onChange={(e) => updateThreshold({ perStepQty: e.target.value })}
                  className={inputCls}
                />
              </div>
            </div>
          )}

          {/* Rounding + Active */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3 sm:col-span-1">
              <label className={labelCls}>Rounding</label>
              <select
                value={form.rounding}
                onChange={(e) => update({ rounding: e.target.value })}
                className={inputCls}
              >
                <option value="none">None</option>
                <option value="up">Round Up</option>
                <option value="down">Round Down</option>
              </select>
            </div>

            <div className="col-span-3 sm:col-span-2 flex items-end">
              <label className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  checked={!!form.active}
                  onChange={(e) => update({ active: e.target.checked })}
                />
                Active
              </label>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className={labelCls}>Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => update({ notes: e.target.value })}
              className={inputCls}
              rows={3}
            />
          </div>

          {/* Cost hint */}
          <div className="rounded-lg border p-3 bg-neutral-50 text-neutral-800 dark:bg-neutral-800 dark:border-neutral-700 dark:text-white/90">
            <div className="text-sm font-semibold mb-1">Cost preview</div>
            {item ? (
              <>
                <div className="text-xs opacity-80 mb-1">
                  Base unit price: <b>${perUnitBase(item).toFixed(4)}</b> • Item tax:{" "}
                  <b>
                    {effectiveTaxFlag === "inclusive"
                      ? `incl ${taxName}`
                      : effectiveTaxFlag === "exclusive"
                      ? `excl ${taxName}`
                      : "no tax"}
                  </b>
                </div>
                <div className="text-xs">
                  Example for <b>10 units produced</b> → Qty used: <b>{qtyUsed}</b>
                </div>
                <div className="mt-1 text-sm">
                  Excl {taxName}: <b>${costEx.toFixed(2)}</b> • Incl {taxName}:{" "}
                  <b>${costIn.toFixed(2)}</b>
                </div>
                <div className="mt-2 text-xs flex items-center gap-2">
                  View as:
                  <select
                    value={displayTaxMode}
                    onChange={(e) => setDisplayTaxMode(e.target.value)}
                    className="px-2 py-1 rounded bg-neutral-200 text-neutral-900 border border-neutral-300
                               dark:bg-neutral-700 dark:text-white dark:border-neutral-600 text-xs"
                  >
                    <option value="inclusive">Inclusive</option>
                    <option value="exclusive">Exclusive</option>
                    <option value="none">No tax</option>
                  </select>
                </div>
              </>
            ) : (
              <div className="text-sm opacity-70">Select an item to preview cost.</div>
            )}
          </div>

          {/* Problems */}
          {!!problems.length && (
            <div className="text-sm text-red-600 dark:text-red-400">
              {problems.map((p, i) => (
                <div key={i}>• {p}</div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded bg-neutral-200 text-neutral-900 hover:bg-neutral-300
                         dark:bg-neutral-700 dark:text-white dark:hover:bg-neutral-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={problems.length > 0}
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
